import Cross from "@icons/cross.svg?react";
import Document from "@icons/document.svg?react";
import Download from "@icons/download.svg?react";
import { Trans } from "@lingui/react/macro";
import { useSuspenseQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { Outlet, useParams } from "react-router-dom";

import { Button, primary } from "@/common/components/button/button";
import { Info } from "@/common/components/info/info";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { useLanguage } from "@/common/hooks/use-language";
import {
  currencyFormatter,
  numberFormatter,
} from "@/common/services/formatter";
import { invariant } from "@/common/utils/invariant";
import { ContractStatus } from "@/generated/digitalnisklady.cz/graphql";

import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { getUnit } from "../shared/utils/crop-unit";
import { contractQuery } from "./contracts.api";
import { path as signaturePath } from "./transactions-and-orders.signature";

const path = "transactions/:contractId";
const Contract = () => {
  const { contractId } = useParams();
  invariant(contractId);
  const locale = useLanguage();
  const contract = useSuspenseQuery(contractQuery({ id: contractId, locale }));

  let ButtonJsx = (
    <SearchParamsLink className={primary} to={signaturePath}>
      <Trans>Podepsat smlouvu</Trans>
    </SearchParamsLink>
  );

  if (contract.data.status === ContractStatus.Failed) {
    ButtonJsx = (
      <Info>
        <Trans>Odmítnuto obchodníkem</Trans>
      </Info>
    );
  }

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <SearchParamsLink to="..">
            <Cross />
          </SearchParamsLink>
        }
      >
        <FullscreenModalLayout.Title>
          <Trans>Detail transakce</Trans>
        </FullscreenModalLayout.Title>
        <PropertiesList
          properties={[
            {
              label: <Trans>Číslo konfirmace</Trans>,
              value: contract.data.externalNumber,
            },
            {
              label: <Trans>Cena</Trans>,
              value: currencyFormatter(contract.data.currency).format(
                contract.data.finalPrice,
              ),
            },
            {
              label: <Trans>Kvartál</Trans>,
              value: contract.data.quarterName,
            },
            {
              label: <Trans>Množství</Trans>,
              value: `${numberFormatter().format(contract.data.amount)} ${getUnit(
                contract.data.crop.unit,
              )}`,
            },
            {
              label: <Trans>Komodita</Trans>,
              value: contract.data.crop.name,
            },
            {
              label: <Trans>Obchodník</Trans>,
              value: (
                <img
                  src={contract.data.trader.logo}
                  style={{ height: "20px" }}
                />
              ),
            },
            {
              label: <Trans>Částka</Trans>,
              value: currencyFormatter(contract.data.currency).format(
                contract.data.amount * contract.data.finalPrice,
              ),
            },
            {
              label: <Trans>Typ</Trans>,
              value: <Trans>Prodej</Trans>,
            },
            {
              label: <Trans>Datum a čas</Trans>,
              value: contract.data.timeCreated.toLocaleString(
                DateTime.DATETIME_SHORT,
              ),
            },
            {
              label: <Trans>Farma</Trans>,
              value: contract.data.company.name,
            },
            {
              label: <Trans>Sklad</Trans>,
              value: contract.data.storage.name,
            },
            {
              label: <Trans>Vlastní doprava</Trans>,
              value: contract.data.customTransport ? (
                <Trans>Ano</Trans>
              ) : (
                <Trans>Ne</Trans>
              ),
            },
            {
              label: <Trans>Poznámka</Trans>,
              value:
                contract.data.note?.length === 0 ? "-" : contract.data.note,
            },
          ]}
        />
        {contract.data.confirmation?.completed ? (
          <ul className="mt-6 gap-y-2 flex flex-column">
            {contract.data.confirmation.documents.map((document) => (
              <li className="flex list-none" key={document.url}>
                <a
                  className="flex basis-full items-center gap-x-2 rounded-2xl bg-can-silver-gray px-4 py-3 font-bold text-can-forest-teal"
                  href={document.url}
                  rel="noreferrer"
                  target="_blank"
                >
                  <Document />
                  <Trans>Smlouva o prodeji</Trans>
                  <Download className="ml-auto" />
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <Button.Container>{ButtonJsx}</Button.Container>
        )}
      </FullscreenModalLayout>
      <Outlet />
    </>
  );
};

export { Contract, path };
